import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { buttonClass, theme } from '../ui/theme';

const getDisplayUrl = (url) => {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/$/, '');
    const base = `${parsed.hostname}${path}`;
    return base.length > 48 ? `${base.slice(0, 45)}...` : base;
  } catch (_) {
    return url.length > 48 ? `${url.slice(0, 45)}...` : url;
  }
};

const getFallbackImage = () => {
  if (typeof window === 'undefined') return '/favicon512.png';
  return `${window.location.protocol}//${window.location.host}/favicon512.png`;
};

const ShareDialog = ({ open, onClose, meta, onCopy }) => {
  const { t } = useTranslation();
  const safeMeta = useMemo(() => ({
    title: meta?.title || '',
    description: meta?.description || '',
    image: meta?.image || '',
    url: meta?.url || '',
    domain: meta?.domain || ''
  }), [meta]);
  const shareTargets = useMemo(() => ([
    { id: 'whatsapp', label: 'WhatsApp', bg: 'bg-emerald-500/90', text: 'W' },
    { id: 'gmail', label: 'Gmail', bg: 'bg-red-500/90', text: 'G' },
    { id: 'reddit', label: 'Reddit', bg: 'bg-orange-500/90', text: 'R' },
    { id: 'facebook', label: 'Facebook', bg: 'bg-blue-600/90', text: 'F' },
    { id: 'embed', label: t('shareDialog.embed'), bg: 'bg-slate-600/90', text: '<>' }
  ]), [t]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const overlayBaseClass = 'fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm';
  const panelBaseClass = [theme.surfaces.modalPanel, 'rounded-2xl shadow-2xl w-full mx-4'].join(' ');
  const previewImage = safeMeta.image || getFallbackImage();

  return createPortal(
    <div className={overlayBaseClass} style={{ zIndex: 100000 }} onClick={onClose}>
      <div
        className={[panelBaseClass, 'max-w-md overflow-hidden'].join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 pt-4 pb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">{t('shareDialog.title')}</h2>
          <button
            onClick={onClose}
            className={buttonClass({ variant: 'ghost', size: 'sm' })}
            aria-label={t('shareDialog.close')}
          >
            <span className="text-white/80">X</span>
          </button>
        </div>

        <div className="px-4 pb-4 space-y-4">
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
            <div className="rounded-xl overflow-hidden mb-3">
              <img
                src={previewImage}
                alt={safeMeta.title || 'share-image'}
                className="w-full h-40 object-cover"
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-semibold text-white truncate">
                {safeMeta.title || t('shareDialog.fallbackTitle')}
              </div>
              {safeMeta.description && (
                <div className="text-xs text-white/65 line-clamp-2">
                  {safeMeta.description}
                </div>
              )}
              <div className="text-xs text-blue-300">
                {safeMeta.domain || getDisplayUrl(safeMeta.url)}
              </div>
            </div>
          </div>

          <div className="flex items-start justify-between gap-3">
            {shareTargets.map((item) => (
              <div key={item.id} className="flex flex-col items-center gap-1.5">
                <div className={`w-11 h-11 rounded-full ${item.bg} flex items-center justify-center text-white text-sm font-semibold shadow`}>
                  {item.text}
                </div>
                <div className="text-[11px] text-white/75">{item.label}</div>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="text-xs text-white/70">{t('shareDialog.linkLabel')}</div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={safeMeta.url}
                className="flex-1 px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-xs text-white/80"
              />
              <button
                onClick={() => onCopy?.(safeMeta.url)}
                className={buttonClass({ variant: 'outline', size: 'sm' })}
              >
                {t('shareDialog.copy')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareDialog;

